import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { OfferPreviewCardContainer } from './OfferPreviewCardContainer';
import { OFFER_PREVIEW_SAFE_AREA } from './constants';
import { useLocalPreviewOffer } from './hooks/useLocalPreviewOffer';
import * as styles from './styles.css';
import { isCompactDesktopPreview } from './utils/isCompactDesktopPreview';
import { closeOfferPreview } from '../../../common/actions/closeOfferPreview/closeOfferPreview';
import { OfferPreviewBalloon } from '../../../common/components/OfferCardPreview/components/OfferPreviewBalloon';
import { OfferPreviewError } from '../../../common/components/OfferCardPreview/components/OfferPreviewError';
import { OfferPreviewLoading } from '../../../common/components/OfferCardPreview/components/OfferPreviewLoading';
import { OfferPreviewModal } from '../../../common/components/OfferCardPreview/components/OfferPreviewModal';
import { getDesktopPreviewSize } from '../../../common/components/OfferCardPreview/utils/getDesktopPreviewSize';
import { getPinAnchor } from '../../../common/constants/clusterPins/getPinAnchor';
import { IDailyRentOfferForMobileSite } from '../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectIsOffersLoading, selectListingOffers } from '../../../common/selectors/listingOffers';
import { TThunkDispatch } from '../../../common/types/redux';
import { selectActiveFeature, selectActiveOfferId, selectIsOfferPreviewOpen } from '../../selectors';
import { useMaxPinsPerCluster } from '../Map/hooks/useMaxPinsPerCluster';

export interface IOfferPreviewContainerProps {
  listingSafeAreaWidth: number;
  map: YMaps.Map;
  ymaps: YMaps.IYMaps;
}

function getPreviewOffer(offers: IDailyRentOfferForMobileSite[], realtyId: number | null | undefined) {
  if (!realtyId) {
    return undefined;
  }

  return offers.find(offer => offer.id === realtyId);
}

export const OfferPreviewContainer = ({ listingSafeAreaWidth, ymaps, map }: IOfferPreviewContainerProps) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const activeFeature = useSelector(selectActiveFeature);
  const activeOfferRealtyId = useSelector(selectActiveOfferId);
  const isPreviewOpen = useSelector(selectIsOfferPreviewOpen);
  const isLoading = useSelector(selectIsOffersLoading);
  const offers = useSelector(selectListingOffers);
  const listingOffer = React.useMemo(() => getPreviewOffer(offers, activeOfferRealtyId), [offers, activeOfferRealtyId]);
  const { isPreviewOfferLoading, offer, retryPreviewOffer } = useLocalPreviewOffer({
    realtyId: activeOfferRealtyId,
    isListingOffersLoading: isLoading,
    listingOffer,
  });
  const isPhone = deviceType === 'phone';

  const onClose = React.useCallback(() => {
    dispatch(closeOfferPreview());
  }, [dispatch]);

  const safeArea = React.useMemo(
    () => ({ ...OFFER_PREVIEW_SAFE_AREA, left: listingSafeAreaWidth }),
    [listingSafeAreaWidth],
  );

  // Режим превью фиксируется при выборе объявления по realtyId.
  // Resize и обновление геометрии пина не меняют режим открытого превью.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isCompactPreview = React.useMemo(() => isCompactDesktopPreview(), [activeOfferRealtyId]);
  const isCompact = !isPhone && isCompactPreview;
  const { width: previewWidth, height: previewHeight } = getDesktopPreviewSize(isCompactPreview);

  if (!isPreviewOpen) {
    return null;
  }

  const content = (() => {
    if (!offer) {
      if (isPreviewOfferLoading) {
        return <OfferPreviewLoading className={!isPhone ? styles['card_desktop'] : undefined} isCompact={isCompact} />;
      }

      return (
        <OfferPreviewError
          className={!isPhone ? styles['card_desktop'] : undefined}
          isCompact={isCompact}
          onClose={onClose}
          onRetry={retryPreviewOffer}
        />
      );
    }

    return (
      <OfferPreviewCardContainer
        className={!isPhone ? styles['card_desktop'] : undefined}
        hasCloseControl={isPhone}
        isCompact={isCompact}
        showButtons
        narrowButtons={!isPhone}
        offer={offer}
      />
    );
  })();

  if (isPhone) {
    return (
      <OfferPreviewModal isOpen={isPreviewOpen} onClose={onClose}>
        {content}
      </OfferPreviewModal>
    );
  }

  if (!activeFeature) {
    return null;
  }

  const anchor = getPinAnchor(activeFeature.properties, maxPinsPerCluster);

  return (
    <OfferPreviewBalloon
      activeFeature={activeFeature}
      anchor={anchor}
      ymaps={ymaps}
      map={map}
      onClose={onClose}
      previewHeight={previewHeight}
      previewWidth={previewWidth}
      safeArea={safeArea}
    >
      {content}
    </OfferPreviewBalloon>
  );
};

OfferPreviewContainer.displayName = 'OfferPreviewContainer';
